import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const ModuleSettingsContext = createContext(null);

export function ModuleSettingsProvider({ children }) {
  const { token } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchModules = useCallback(async () => {
    if (!token) {
      setModules([]);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch("/api/admin/modules", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setModules(json.modules || []);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to load module settings");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const enabledMap = useMemo(() => {
    const m = {};
    for (const row of modules) m[row.module_key] = !!row.enabled;
    return m;
  }, [modules]);

  // While loading we optimistically return `true` so the UI doesn't briefly
  // hide all modules on first paint.
  const isEnabled = useCallback(
    (key) => (loading ? true : enabledMap[key] !== false),
    [loading, enabledMap]
  );

  const setEnabled = useCallback(
    async (key, enabled) => {
      const res = await fetch(`/api/admin/modules/${encodeURIComponent(key)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setModules((prev) =>
        prev.map((m) => (m.module_key === key ? json.module : m))
      );
    },
    [token]
  );

  return (
    <ModuleSettingsContext.Provider
      value={{ modules, loading, error, isEnabled, setEnabled, refresh: fetchModules }}
    >
      {children}
    </ModuleSettingsContext.Provider>
  );
}

export function useModuleSettings() {
  const ctx = useContext(ModuleSettingsContext);
  if (!ctx) throw new Error("useModuleSettings must be used within ModuleSettingsProvider");
  return ctx;
}
